import { ChangeDetectionStrategy, Component, ViewChild, computed, effect, inject, output, signal, viewChild, OnDestroy } from '@angular/core';
import { FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { UserService } from '../../services/user.service';
import { BaseComponent } from '../../../../../core/shared/common/base.component';
import { catchError, of, switchMap, takeUntil } from 'rxjs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { SVGIcon, xIcon } from '@progress/kendo-svg-icons';
import { IUser } from '../../../../../core/models/entities';
import { FormService } from './services/form.service';

import { NotificationService } from '@progress/kendo-angular-notification';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { CommonModule } from '@angular/common';
import { User } from '../../../../../core/models/user';
import { CyflareAutocompleteComponent } from '../../../../../core/components/cyflare-autocomplete/cyflare-autocomplete.component';
import { PARTNER_ADMIN_OPTION, PARTNER_ADMIN_ROLE, ROLE_OPTIONS, mergeGroups, roleOf } from '../../utils/user-groups';
import { isCyflareAdmin } from '../../../../../core/shared/cyflare-admin';
import { PartnerAccessChange, PartnerAccessComponent } from '../partner-access/partner-access.component';
import { PartnerAccessAdminService } from '../../../../../core/services/partner-portal/partner-access-admin.service';

@Component({
  standalone: true,
  selector: 'cyflare-user',
  imports: [FormsModule, ReactiveFormsModule, InputsModule, LabelModule, ButtonsModule, DropDownsModule, DialogsModule, CommonModule, CyflareAutocompleteComponent, PartnerAccessComponent],
  providers: [
    NotificationService,
    UserService,
    FormService,
  ],
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'], // Note the plural 'styleUrls'
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserComponent extends BaseComponent implements OnDestroy {
  private userService = inject(UserService);
  private forms = inject(FormService);
  public xIcon: SVGIcon = xIcon;
  private notificationService = inject(NotificationService);
  private partnerAccessAdmin = inject(PartnerAccessAdminService);
  public user = signal<IUser>(User.createUser());
  public userForm = this.forms.userForm();
  public roles: Array<string> = ['Admin', 'User'];
  /** Partner Admin can't be assigned here; users who hold it see it read-only. */
  public readonly isPartnerAdmin = computed(() => roleOf(this.user().groups) === PARTNER_ADMIN_ROLE);
  public readonly roleOptions = computed(() => (this.isPartnerAdmin() ? [PARTNER_ADMIN_OPTION] : ROLE_OPTIONS));
  /** Only Cyflare admins grant portal access, and Cyflare admins already have it. */
  private readonly signedInIsCyflareAdmin = isCyflareAdmin(inject(User));
  public readonly showPartnerAccess = computed(() => this.signedInIsCyflareAdmin && roleOf(this.user().groups) !== 'admin');
  public readonly selectedOrgAccountType = signal<number | null>(null);
  private readonly partnerAccess = viewChild(PartnerAccessComponent);
  private readonly orgPicker = viewChild(CyflareAutocompleteComponent);
  close = output<void>();
  public opened = false;

  public orgs = signal<{ id: number; name: string }[]>([]);
  public cachedUser: IUser | undefined;
  public defaultItem: any = { display_text: 'Select Organization', id: 0 };
  private isDestroyed = false;

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect() {
    effect(() => {
      const user = this.user();
      this.cachedUser = { ...user };

      const orgs = user.organizations?.map((org) => org?.id).filter((id): id is number => id !== null && id !== undefined) ?? [];

      const role = roleOf(user.groups);
      this.userForm.patchValue({
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        role,
        primary_organization_id: user.primary_organization?.id,
      });
      if (role === PARTNER_ADMIN_ROLE) {
        this.userForm.controls.role.disable({ emitEvent: false });
      } else {
        this.userForm.controls.role.enable({ emitEvent: false });
      }

      this.userForm.setControl('organizations', new FormArray<FormControl<number | null>>([...orgs.map((orgId) => new FormControl(orgId))]));

      if (user.primary_organization?.id) {
        this.defaultItem = {
          id: user.primary_organization.id,
          display_text: user.primary_organization.name,
        };
      } else {
        this.defaultItem = null;
      }
    });
  }

  override ngOnDestroy(): void {
    this.isDestroyed = true;
    super.ngOnDestroy?.(); // call parent destroy logic if defined
  }

  onOrgDropdownChange($event: any) {
    if (!$event) return;
    this.selectedOrgAccountType.set($event.account_type ?? null);
    const organizationId = $event.id;
    const organizationsArray = this.userForm.get('organizations') as FormArray;

    if (organizationsArray.length > this.cachedUser!.organizations!.length) {
      organizationsArray.removeAt(organizationsArray.length - 1);
    }

    const alreadyExists = this.cachedUser!.organizations!.find((org) => org.id === organizationId);
    if (!alreadyExists) {
      organizationsArray.push(new FormControl(organizationId));
    }
  }

  public open(): void {
    this.opened = true;
  }

  /** The edited role plus the groups this editor doesn't manage, which are kept as they are. */
  private groupsFromForm(): string[] {
    const role = this.isPartnerAdmin() ? PARTNER_ADMIN_ROLE : this.userForm.controls.role.value;
    return mergeGroups(this.cachedUser?.groups, role);
  }

  public save($event: Event) {
    const { role, add_to_child_org, ...formValueWithoutRole } = this.userForm.value;
    const partnerAccess = this.pendingPartnerAccess();
    const transformedValue = {
      ...formValueWithoutRole,
      groups: this.groupsFromForm(),
      add_to_child_org: add_to_child_org ?? false,
    };

    console.log('Form Value:', this.userForm.value); // Debugging line to check form values
    console.log('Transformed Value:', transformedValue); // Debugging line to check transformed values

    this.userService
      .addUser(transformedValue)
      .pipe(
        switchMap((created) => this.savePartnerAccess(created.id, partnerAccess, 'added')),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: () => {
          if (!this.isDestroyed) {
            this.clear();
            setTimeout(() => this.close.emit(), 0); // Safe async emit
          }
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  /** Admins already have the portal, so their toggles are ignored. */
  private pendingPartnerAccess(): PartnerAccessChange | null {
    return this.userForm.controls.role.value === 'admin' ? null : (this.partnerAccess()?.pendingChange() ?? null);
  }

  /**
   * Runs after the user is saved, so a primary org changed in the same save counts toward
   * eligibility. The user is already saved by then, so a failure is reported, not thrown.
   */
  private savePartnerAccess(userId: number, change: PartnerAccessChange | null, savedAs: 'added' | 'updated') {
    if (!change) return of(null);
    return this.partnerAccessAdmin.set(userId, change.portal, change.sales).pipe(
      catchError(() => {
        this.notificationService.show({
          content: `User ${savedAs}, but Partner Portal access wasn't saved. Open the user to try again.`,
          type: { style: 'error' },
        });
        return of(null);
      })
    );
  }

  public clear() {
    this.userForm.reset();
    this.orgPicker()?.clear();
    this.selectedOrgAccountType.set(null);
  }

  public closeDialog(status: string): void {
    console.log(`Dialog result: ${status}`);
    this.opened = false;
  }

  public cancel() {
    this.userForm.reset();
    this.close.emit();
  }

  update($event: Event) {
    $event.preventDefault();
    let payload = {
      first_name: this.userForm.value.first_name,
      last_name: this.userForm.value.last_name,
      email: this.userForm.value.email,
      organizations: this.userForm.value.organizations,
      primary_organization_id: this.userForm.value.primary_organization_id,
      groups: this.groupsFromForm(),
      add_to_child_org: this.userForm.value.add_to_child_org ?? false,
    };
    const id = this.userForm.value.id!;
    const partnerAccess = this.pendingPartnerAccess();
    this.userService
      .updateUser(id, payload)
      .pipe(
        switchMap(() => this.savePartnerAccess(id, partnerAccess, 'updated')),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: () => {
          if (!this.isDestroyed) {
            this.clear();
            setTimeout(() => this.close.emit(), 0); // Safe async emit
          }
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  delete($event: Event) {
    $event.preventDefault();
    const id = this.userForm.value.id!;
    this.userService
      .deleteUser(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.clear();
          this.closeDialog('delete confirmed');
          this.close.emit();
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  resetPassword($event: Event) {
    $event.preventDefault();
    const id = this.userForm.value.id;

    if (!id) {
      console.error('User ID is required to reset the password.');
      this.notificationService.show({
        content: 'User ID is required to reset the password.',
        type: { style: 'error' },
      });
      return;
    }

    this.userService
      .resetPassword(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.notificationService.show({
            content: "Password reset link sent to user's email.",
            type: { style: 'success' },
          });
        },
        error: (error) => {
          console.error('Error resetting password:', error);
          this.notificationService.show({
            content: 'Failed to send password reset link. Please try again.',
            type: { style: 'error' },
          });
        },
      });
  }

  resetMfa($event: Event) {
    $event.preventDefault();
    const id = this.userForm.value.id!;
    this.userService
      .resetMFA(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.clear();
          this.close.emit();
        },
        error: (error) => {
          console.error(error);
        },
      });
  }
}
